import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { AnimatePresence, animate, motion, useReducedMotion } from "framer-motion"
import { isDesktopLike } from "../atoms/DesktopLikeView"
import { theme } from "../settings/config"

// Il posto di una scheda nel profilo. Tenendo premuto il titolo — in alto,
// non in mezzo, dove ci sono bottoni e righe da toccare — si entra nel
// riordino. Mentre si tiene premuto la scheda si stringe un poco e dice cosa
// sta per succedere; sul PC lo dice anche passandoci sopra col mouse, perché
// una pressione lunga col mouse non viene in mente a nessuno.

const PRESS_MS = 500
// quanto si tiene prima che la scheda risponda: un tocco veloce resta un tocco
const FEEDBACK_MS = 150
const HOVER_HINT_MS = 900
// "in alto": l'intestazione delle schede è alta così, col tondo dell'icona
const HEADER_HEIGHT = 80
// un dito che si sposta sta scorrendo la pagina, non tenendo premuto
const MOVE_TOLERANCE = 8

// Dopo una pressione lunga il dito si alza, e il browser manda il click a
// quello che c'era sotto: a riordino aperto non c'è più niente da toccare lì.
// Si ferma solo quel click, che arriva subito dopo il dito alzato: il tocco
// dopo è già un'altra cosa.
const swallowReleaseClick = () => {
	const stop = event => {
		event.stopPropagation()
		event.preventDefault()
	}
	const release = () => {
		window.removeEventListener("pointerup", release, { capture: true })
		window.removeEventListener("pointercancel", release, { capture: true })
		setTimeout(() => window.removeEventListener("click", stop, { capture: true }), 0)
	}
	window.addEventListener("click", stop, { capture: true })
	window.addEventListener("pointerup", release, { capture: true })
	window.addEventListener("pointercancel", release, { capture: true })
}

// Tornando dal riordino la scheda si riapre dalla sua riga: parte alta come
// la riga e si allunga fino alla sua altezza, mentre il contenuto viene su.
// Le schede tengono un margine di 1rem sopra e sotto, che di solito si
// fonde con quello della vicina; mentre si allunga resta dentro (la scheda
// è tagliata), e lo si compensa fuori con un margine negativo uguale, così
// la distanza dalle altre schede non cambia né durante né dopo.
const ROW_HEIGHT = 60
const EXPAND = { duration: 0.42, ease: [0.04, 0.62, 0.23, 0.98] }

// Allunga `inner` dall'altezza di una riga (`fromRow`) o da niente fino alla
// sua, col contenuto che viene su; ridà la funzione che la ferma.
export const expandOnArrival = (inner, { fromRow }) => {
	const rem = parseFloat(window.getComputedStyle(document.documentElement).fontSize)
	Object.assign(inner.style, { display: "flow-root", overflow: "hidden", margin: `-${rem}px 0` })
	const full = inner.offsetHeight
	const from = (fromRow ? ROW_HEIGHT : 0) + 2 * rem
	const controls = [
		animate(inner, { height: [from, full] }, EXPAND),
		animate(inner, { opacity: [fromRow ? 0.35 : 0, 1] }, { duration: 0.3 }),
	]
	const reset = () =>
		Object.assign(inner.style, { display: "", overflow: "", margin: "", height: "", opacity: "" })
	// se intanto il contenuto è cresciuto (una lettura arrivata mentre si
	// apriva), la scheda ci arriva scivolando, non con uno scatto alla fine
	controls[0].then(() => {
		const grown = inner.scrollHeight
		if (Math.abs(grown - full) < 2) return reset()
		controls.push(animate(inner, { height: grown }, { duration: 0.25, ease: EXPAND.ease }))
		controls[controls.length - 1].then(reset)
	})
	return () => {
		controls.forEach(control => control.stop())
		reset()
	}
}

const useExpandFromRow = (innerRef, appear) => {
	const reduceMotion = useReducedMotion()
	useLayoutEffect(() => {
		const inner = innerRef.current
		// una scheda che non ha niente da mostrare non si riapre
		if (!appear || reduceMotion || !inner?.firstElementChild) return
		return expandOnArrival(inner, { fromRow: true })
		// solo all'arrivo
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
}

function ProfileSlot({ cardID, appear = false, onLongPress, children }) {
	const ref = useRef(null)
	const press = useRef(null)
	const hoverTimer = useRef(null)
	const [pressing, setPressing] = useState(false)
	const [hovering, setHovering] = useState(false)
	const innerRef = useRef(null)
	useExpandFromRow(innerRef, appear)

	const inHeader = event => {
		const rect = ref.current?.getBoundingClientRect()
		return rect && event.clientY - rect.top <= HEADER_HEIGHT
	}

	const cancelPress = () => {
		if (!press.current) return
		clearTimeout(press.current.feedback)
		clearTimeout(press.current.timer)
		press.current = null
		setPressing(false)
	}

	const hideHint = () => {
		clearTimeout(hoverTimer.current)
		hoverTimer.current = null
		setHovering(false)
	}

	useEffect(
		() => () => {
			if (press.current) {
				clearTimeout(press.current.feedback)
				clearTimeout(press.current.timer)
			}
			clearTimeout(hoverTimer.current)
		},
		[],
	)

	if (!onLongPress) return <div data-profile-card={cardID}>{children}</div>

	const onPointerDown = event => {
		if (event.button !== 0 || !inHeader(event)) return
		cancelPress()
		press.current = {
			x: event.clientX,
			y: event.clientY,
			feedback: setTimeout(() => setPressing(true), FEEDBACK_MS),
			timer: setTimeout(() => {
				cancelPress()
				hideHint()
				swallowReleaseClick()
				navigator.vibrate?.(15)
				onLongPress()
			}, PRESS_MS),
		}
	}

	const onPointerMove = event => {
		if (
			press.current &&
			Math.hypot(event.clientX - press.current.x, event.clientY - press.current.y) >
				MOVE_TOLERANCE
		)
			cancelPress()

		if (event.pointerType !== "mouse") return
		if (!inHeader(event)) hideHint()
		else if (!hoverTimer.current)
			hoverTimer.current = setTimeout(() => setHovering(true), HOVER_HINT_MS)
	}

	return (
		<motion.div
			ref={ref}
			data-profile-card={cardID}
			onPointerDown={onPointerDown}
			onPointerMove={onPointerMove}
			onPointerUp={cancelPress}
			onPointerCancel={cancelPress}
			onPointerLeave={() => {
				cancelPress()
				hideHint()
			}}
			// il menu del telefono sul testo tenuto premuto: qui il dito
			// premuto vuol dire un'altra cosa
			onContextMenu={event => inHeader(event) && event.preventDefault()}
			animate={{ scale: pressing ? 0.97 : 1 }}
			transition={{ duration: 0.2 }}
			style={{
				position: "relative",
				// sul telefono una pressione lunga selezionerebbe il testo
				...(!isDesktopLike && { userSelect: "none", WebkitUserSelect: "none" }),
				WebkitTouchCallout: "none",
			}}>
			<AnimatePresence>
				{(pressing || hovering) && (
					<motion.div
						role="tooltip"
						initial={{ opacity: 0, y: 4, x: "-50%" }}
						animate={{ opacity: 1, y: 0, x: "-50%" }}
						exit={{ opacity: 0, y: 4, x: "-50%" }}
						transition={{ duration: 0.15 }}
						style={{
							position: "absolute",
							left: "50%",
							top: "-1.6rem",
							zIndex: 2,
							padding: "0.4rem 0.8rem",
							borderRadius: "12px",
							background: theme.colors.gunmetal_fill,
							color: "white",
							fontSize: "0.82rem",
							fontWeight: 500,
							whiteSpace: "nowrap",
							boxShadow: theme.shadows.lift,
							pointerEvents: "none",
						}}>
						Tieni premuto per riordinare
					</motion.div>
				)}
			</AnimatePresence>
			<div ref={innerRef}>{children}</div>
		</motion.div>
	)
}

export default ProfileSlot
